// src/hooks/useAdjusterEvents.js
// Compartido entre ClaimStatusGate.jsx (widget "Próximas reuniones" en la
// portada de Claim Status) y ClaimAgendaPage.jsx ("Mi agenda", calendario
// del mes completo) — mismo criterio de qué eventos ve cada usuario
// (propios, o todos si es admin/supervisor), mezclando las citas de la app
// (Firestore) con las reuniones que solo existen en Zoho. Evita mantener
// esta lógica duplicada en dos pantallas.
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getEventsForAdjuster, usesZoho } from '../services/claimEventService';
import { listZohoCalendarEvents } from '../services/zohoCalendarService';

export const isPastEvent = (event) => {
  if (!event.date) return false;
  return new Date(`${event.date}T${event.time || '23:59'}`).getTime() < Date.now();
};

// monthKey: 'YYYY-MM' para acotar la consulta a Zoho (que filtra por
// rango). Las citas de la app no tienen ese límite — se traen todas y se
// filtran en el cliente donde haga falta.
export const useAdjusterEvents = (monthKey) => {
  const { isAuthenticated, isAdmin, userCompany, userAdjusterName, userCanViewAllClaims } = useAuth();
  const [appEvents, setAppEvents] = useState([]);
  const [zohoEvents, setZohoEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zohoError, setZohoError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    setLoading(true);
    getEventsForAdjuster({ isAdmin, companyName: userCompany, adjusterName: userAdjusterName, canViewAll: userCanViewAllClaims })
      .then(list => { if (active) setAppEvents(list); })
      .catch(() => { if (active) setAppEvents([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isAuthenticated, isAdmin, userCompany, userAdjusterName, userCanViewAllClaims]);

  useEffect(() => {
    if (!isAuthenticated || (!usesZoho(userCompany || '') && !isAdmin)) { setZohoEvents([]); return; }
    let active = true;
    listZohoCalendarEvents(monthKey)
      .then(list => { if (active) { setZohoEvents(list); setZohoError(''); } })
      .catch(err => { if (active) { setZohoEvents([]); setZohoError(err.message || 'Zoho error'); } });
    return () => { active = false; };
  }, [isAuthenticated, isAdmin, userCompany, monthKey]);

  const knownZohoUids = new Set(appEvents.map(e => e.zohoUid).filter(Boolean));
  const events = [
    ...appEvents,
    ...zohoEvents
      .filter(e => !knownZohoUids.has(e.zohoUid))
      .map(e => ({
        id: `zoho-${e.zohoUid}`,
        claimRecordId: null,
        type: null,
        title: e.title,
        date: e.date,
        time: e.time,
        location: e.location,
        notes: e.description,
        confirmed: null,
        zohoUid: e.zohoUid,
        zohoUrl: e.url,
        source: 'zoho'
      }))
  ];

  return { events, loading, zohoError };
};
